/**
 * Address validation utility functions
 * Validates delivery address fields according to US address standards
 */

// Valid US state codes
const VALID_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

/**
 * Validates street address
 * Allows alphanumeric characters, spaces, commas, hyphens, periods, and hash symbols
 * @param {string} street - Street address to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateStreet = (street) => {
  if (!street || street.trim() === '') {
    return 'Street address is required';
  }
  
  // Allow alphanumeric, spaces, commas, periods, hyphens, and # for apartment/suite numbers
  const streetRegex = /^[a-zA-Z0-9\s,.\-#]+$/;
  
  if (!streetRegex.test(street)) {
    return 'Street address contains invalid characters';
  }
  
  if (street.trim().length < 3) {
    return 'Street address must be at least 3 characters';
  }
  
  return null;
};

/**
 * Validates city name
 * Allows letters, spaces, hyphens, and apostrophes
 * @param {string} city - City name to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateCity = (city) => {
  if (!city || city.trim() === '') {
    return 'City is required';
  }
  
  // Allow letters, spaces, hyphens, and apostrophes (for cities like "O'Fallon")
  const cityRegex = /^[a-zA-Z\s\-']+$/;
  
  if (!cityRegex.test(city)) {
    return 'City name contains invalid characters';
  }
  
  if (city.trim().length < 2) {
    return 'City name must be at least 2 characters';
  }
  
  return null;
};

/**
 * Validates US state code
 * Must be exactly 2 uppercase letters and a valid US state code
 * @param {string} state - State code to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateState = (state) => {
  if (!state || state.trim() === '') {
    return 'State is required';
  }
  
  // Must be exactly 2 uppercase letters
  const stateRegex = /^[A-Z]{2}$/;
  
  if (!stateRegex.test(state.trim())) {
    return 'State must be 2 uppercase letters (e.g., FL, NY)';
  }
  
  // Check if it's a valid US state code
  if (!VALID_STATE_CODES.includes(state.trim())) {
    return 'Invalid state code. Please select a valid US state';
  }
  
  return null;
};

/**
 * Validates US ZIP code
 * Accepts 5-digit format (12345) or 5+4 format (12345-6789)
 * @param {string} zipCode - ZIP code to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateZipCode = (zipCode) => {
  if (!zipCode || zipCode.trim() === '') {
    return 'ZIP code is required';
  }
  
  // Accept 5 digits or 5+4 format
  const zipRegex = /^\d{5}(-\d{4})?$/;
  
  if (!zipRegex.test(zipCode.trim())) {
    return 'ZIP code must be 5 digits (12345) or 9 digits (12345-6789)';
  }
  
  return null;
};

/**
 * Validates all address fields
 * @param {Object} address - Address object with street, city, state, zipCode
 * @returns {Object} - Object with field names as keys and error messages as values
 */
export const validateAddress = (address) => {
  return {
    street: validateStreet(address.street),
    city: validateCity(address.city),
    state: validateState(address.state),
    zipCode: validateZipCode(address.zipCode),
  };
};

/**
 * Checks if address has any validation errors
 * @param {Object} errors - Errors object from validateAddress
 * @returns {boolean} - True if there are any errors
 */
export const hasAddressErrors = (errors) => {
  return Object.values(errors).some(error => error !== null);
};
